import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArticleTag,
  articleTagsApi,
} from '@texas/api/endpoints/articleTagsApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { ArticleTagModal } from './ArticleTagModal';

export function ArticleTags() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const canEdit = hasRole(currentUser.role, [Role.SystemAdministrator]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<ArticleTag | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(
    articleTagsApi.getAll,
  );

  useEffect(() => {
    refetch(currentUser.organizationId);
  }, [currentUser.organizationId, refetch]);

  const tags = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((tag) => tag.name.toLowerCase().includes(normalized))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={t('configuration.navigation.articleTags')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
      action={
        canEdit ? (
          <Button
            variant="entityDirectoryPrimary"
            onClick={() => {
              setSelected(null);
              onOpen();
            }}
          >
            {t('configuration.newArticleTag')}
          </Button>
        ) : undefined
      }
    >
      <ArticleTagModal
        canEdit={canEdit}
        isOpen={isOpen}
        onClose={onClose}
        onSaved={() => refetch(currentUser.organizationId)}
        tag={selected}
      />
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && tags.length === 0 && (
              <Tr>
                <Td>{t('general.noData')}</Td>
              </Tr>
            )}
            {tags.map((tag) => (
              <Tr
                key={tag.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelected(tag);
                  onOpen();
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelected(tag);
                  onOpen();
                }}
              >
                <Td>{tag.name}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
